<template>
    <admin-layout>
        <section class="px-4 md:px-5 py-4 md:py-7 space-y-7">
            <custom-info-header icon="typcn:flow-merge" title="Answer Bots"
                description="Manage and organize your automated response bots">
                <template v-slot:action>
                    <custom-button
                                type="button"
                                size="md"
                                label="Set Up"
                                @click="() => isCreateBotModal = true" />
                </template>
            </custom-info-header>

            <article class="mt-10">
                <custom-table 
                    :headers="[
                        'Name',
                        'Bot Type',
                        'Created Date',
                        ''
                    ]" 
                    :loading="{
                        isLoading: isLoading || isDeleteLoading,
                        barRows: 6,
                        barCols: 3
                    }" 
                    :isEmpty="!isLoading && bots.length <= 0">
                    <template v-slot:body>
                        <tr v-for="bot in bots" :key="bot.id"
                            class="h-16 border-t border-gray-100 rounded">
                            <custom-table-td>
                                <router-link :to="{ name: 'answerBotCreate', params: { guid: bot.guid } }"
                                    class="text-blue-600 flex items-center gap-1 hover:underline">
                                    <em class="not-italic">{{ bot.name }}</em>
                                    <Icon icon="lucide:external-link" />
                                </router-link>
                            </custom-table-td>
                            <custom-table-td>{{ bot.botType.name }}</custom-table-td>
                            <custom-table-td>{{ formatDate(bot.createdDate) }}</custom-table-td>
                            <custom-table-td type="template">
                                <custom-dropdown placement="right">
                                    <template v-slot:button>
                                        <button class="flex px-5 py-3 rounded-md bg-transparent text-gray-500">
                                            <Icon icon="mingcute:more-2-fill" class="text-xl" />
                                        </button>
                                    </template>

                                    <template v-slot:content>
                                        <section>
                                            <router-link :to="{
                                                name: 'answerBotCreate',
                                                params: {
                                                    guid: bot.guid
                                                }
                                            }" class="px-4 py-3 hover:bg-primary-50 block w-full">Update</router-link>
                                        </section>
                                        <section>
                                            <button type="button" class="text-left px-4 py-3 hover:bg-primary-50 block w-full" @click="onDeleteHandle(bot.guid, index)">
                                                Disable
                                            </button>
                                        </section>
                                    </template>
                                </custom-dropdown>
                            </custom-table-td>
                        </tr>
                    </template>
                </custom-table>
            </article>
        </section>


        <custom-modal 
                title="Bot"
                subTitle="Create a new bot for your brand" 
                :is-open="isCreateBotModal"
                @cancel="() => isCreateBotModal = false">
                <template v-slot:body>
                    <form @submit.stop.prevent="onCreateHandle" class="grid grid-cols-2 gap-x-4 gap-y-3">
                        <div class="col-span-full">
                            <custom-select
                                    id="brandId"
                                    placeholder="Select a brand"
                                    label="Brand"
                                    :data="brands"
                                    v-model="bot.idBrand"
                                    :setLabel="(model) => model.name"
                                    :setCode="(model) => model.id"
                                    :isLoading="isBrandLoading"
                                    :validate="validate$.idBrand"
                            />
                        </div>
                        <div class="col-span-full">
                            <custom-input
                                    id="botName"
                                    type="text"
                                    label="Name"
                                    v-model="bot.name"
                                    placeholder="Name"
                                    :validate="validate$.name"
                            />
                        </div>

                        <div class="col-span-full">
                            <custom-select
                                    id="botTypeId"
                                    placeholder="Select a Bot Type"
                                    label="Bot Type"
                                    :data="botTypes"
                                    :setLabel="(model) => model.name"
                                    :setCode="(model) => model.id"
                                    v-model="bot.idBotType"
                                    :isLoading="isBotTypeLoading"
                                    :validate="validate$.idBotType"
                                />
                        </div>

                        <div class="col-span-full">
                            <custom-button 
                                    type="submit"
                                    :isLoading="isCreateLoading"
                                    label="Create"
                                    class="float-right"
                                    size="xs"
                            />
                        </div>
                    </form>
                </template>
        </custom-modal>

    </admin-layout>
</template>

<script setup>
import { onMounted, ref } from 'vue';
import { Icon } from "@iconify/vue";
import AdminLayout from '@/app/layouts/AdminLayout'
import CustomInfoHeader from '@/app/components/CustomInfoHeader'
import CustomDropdown from '@/app/components/CustomDropdown'
import CustomButton from '@/app/components/CustomButton'
import CustomModal from '@/app/components/CustomModal'
import CustomInput from '@/app/components/CustomInput'
import CustomSelect from '@/app/components/CustomSelect'
import CustomTable from '@/app/components/CustomTable'
import CustomTableTd from '@/app/components/CustomTableTd'
import { formatDate } from '@/core/helper/utility.js'
import { useBot } from '@/core/composables/useBot'
import { useBotType } from '@/core/composables/useBotType'
import { useBrand } from '@/core/composables/useBrand'

const isCreateBotModal = ref(false);
const {
    isLoading,
    isCreateLoading,
    isDeleteLoading,
    validate$,
    bot,
    bots,
    getBots,
    onCreateHandle,
    onDeleteHandle
} = useBot()

const {
    isLoading: isBotTypeLoading,
    botTypes,
    getBotTypes
} = useBotType()

const {
    isLoading: isBrandLoading,
    brands,
    getBrands
} = useBrand()

onMounted(async () => {
    await getBots();
    await getBotTypes();
    await getBrands();
})
</script>